import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  useOutletContext,
} from 'react-router-dom';
import { Control, useForm } from 'react-hook-form';
import { SendMessage } from 'react-use-websocket';
import {
  BaseellaStatus,
  InternalSidepanel,
  PermissionEnum,
  usePrevious,
  ajvResolver,
  BaseTransaction,
  ResponseError,
  responseErrorsResolver,
  formatBalance,
  SelectItemProps,
  Response,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import StatusesUrl from '../../types/statuses-url';
import {
  changePanelState,
  clearInternalTransactionState,
} from './store/slices/transaction';
import {
  clearState,
  clearExpandedGeneralLedgerTransactionState,
} from '../transactions/store/slice';
import { setCollapseAllRows } from './store/slices/list';
import ROUTES from '../../routes';
import { clearStatusesState } from '../../store/statuses/slice';
import { clearSignaturesState } from '../../store/signature/slice';
import useBaseRequests from '../transactions/hooks/use-base-requests';
import useRequests from './hooks/use-request';
import { CheckTransactionStatus } from '../../types/check-transaction';
import { MfaToken, MfaType } from '../../store/types';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import { Balance } from '../../types/account';
import { useLazyGetTransactionsAccountBalanceQuery } from '../../store/dictionaries/services';
import { useLazyGetAllAccountalancesQuery } from '../transactions/store/services';

const schema = {
  type: 'object',
  properties: {
    transferDate: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Date is required',
    },
    sum: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Amount is required',
    },
    currencyId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency is required',
    },
    recipientAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Recipient Account is required',
    },
    senderAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Sender Account is required',
    },
    info: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Info is required',
    },
    documentNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Document # is required',
    },
  },
  required: [
    'transferDate',
    'sum',
    'currencyId',
    'info',
    'recipientAccountNumber',
    'documentNumber',
    'senderAccountNumber',
  ],
};

const defaultFormValues: any = {
  transferDate: '',
  sum: '',
  currencyId: '',
  info: '',
  recipientAccountNumber: '',
  documentNumber: '',
  senderAccountNumber: '',
};

const formatFormDate = (date: string) => {
  const dateFormatted = new Date(date);

  dateFormatted.setHours(0);
  dateFormatted.setMinutes(0);
  dateFormatted.setSeconds(0);
  dateFormatted.setMilliseconds(0);

  return dateFormatted;
};

const InternalTransaction = (): React.JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { transactionId } = useParams();
  const { search, state: locationState } = useLocation();
  const [statusChangeError, setStatusChangeError] = useState('');
  const [backPath, setBackPath] = useState('');
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [checkError, setCheckError] = useState('');
  const [statusCheckError, setStatusCheckError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [createdTransactionId, setCreatedTransactionId] = useState('');
  const [amount, setAmount] = useState<string | undefined>();
  const [availableCurrencies, setAvailableCurrencies] = useState<
    SelectItemProps[]
  >([]);
  const [senderBalances, setSenderBalances] = useState<Balance[]>([]);
  const [recipientBalances, setRecipientBalances] = useState<Balance[]>([]);
  const [senderBalance, setSenderBalance] = useState<number>();
  const [recipientBalance, setRecipientBalance] = useState<number>();
  const [warningActionError, setWarningActionError] = useState<
    { warningId: string; error: string } | undefined
  >(undefined);

  const {
    internal: {
      transaction: {
        transaction,
        panelIsOpen,
        warnings,
        glTransactions,
        aml,
        recipientCompanyOfficials,
        senderCompanyOfficials,
        warningsInfo,
      },
      list: { selectedCount },
    },
    statuses: { statuses, statusLogs },
    transactions: {
      document: documentItem,
      expandedGLTransaction,
      documents,
      expandedGLTransactionCreditBalance,
      expandedGLTransactionDebitBalance,
      currentEmployee,
      employee,
      chats,
      transactionChangeLogs,
      transactionChangeLogsPagination,
      documentsPagination,
      transactionComments,
      transactionCommentsPagination,
    },
    signatures: { signatures },
    transactionsDictionaries: {
      activePermissions,
      transactionsTypes,
      currenciesList,
      currencies,
      baseCurrency,
      overdraftEnabled,
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const {
    control,
    formState: { dirtyFields, errors },
    reset,
    setError,
    getValues,
    watch,
    clearErrors,
    handleSubmit,
    setValue,
  } = useForm({
    resolver: ajvResolver(schema),
    defaultValues: {
      ...defaultFormValues,
    },
  });

  const fields = watch();
  const isDirty = Object.keys(dirtyFields).length !== 0;

  const transferDate = watch('transferDate');
  const senderAccountNumber = watch('senderAccountNumber');
  const recipientAccountNumber = watch('recipientAccountNumber');
  const currencyId = watch('currencyId');

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    isLoading: recipientAccountsIsLoading,
    accountsList: recipientAccountsList,
    setAccountsList: setRecipientAccountsList,
    setAutocompleteList: setRecipientAutocompleteList,
  } = useGetAccountsAutocompleteList(
    `&isClientAccount=true${!transactionId ? '&closed=false' : ''}`,
    'fullNumber',
  );

  const {
    autocompleteList: senderAccounts,
    onSearch: onSearchSenderAccounts,
    isLoading: senderAccountsIsLoading,
    accountsList: senderAccountsList,
    setAccountsList: setSenderAccountsList,
    setAutocompleteList: setSenderAutocompleteList,
  } = useGetAccountsAutocompleteList(
    `&isClientAccount=true${!transactionId ? '&closed=false' : ''}`,
    'fullNumber',
  );

  const [
    getSenderAccountBalance,
    {
      isLoading: senderAccountBalanceIsLoading,
      isFetching: senderAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const [
    getRecipientAccountBalance,
    {
      isLoading: recipientAccountBalanceIsLoading,
      isFetching: recipientAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const [getAllSenderAccountBalances] = useLazyGetAllAccountalancesQuery();

  const [getAllRecipientAccountBalances] = useLazyGetAllAccountalancesQuery();

  const getAllSenderAccountBalancesAction = useCallback(
    async (id: string) => {
      const response = (await getAllSenderAccountBalances({
        id,
      })) as { data: Response<Balance> };

      if (response?.data?.data) {
        setSenderBalances(
          response?.data?.data?.edges?.map((item) => item.node),
        );
      }
    },
    [senderAccountNumber],
  );

  const getAllRecipientAccountBalancesAction = useCallback(
    async (id: string) => {
      const response = (await getAllRecipientAccountBalances({
        id,
      })) as { data: Response<Balance> };

      if (response?.data?.data) {
        setRecipientBalances(
          response?.data?.data?.edges?.map((item) => item.node),
        );
      }
    },
    [recipientAccountNumber],
  );

  const getSenderAccountBalanceAction = useCallback(
    async (number?: string) => {
      const currentAccount = senderAccountsList.find(
        (item) =>
          item.fullNumber === number || item.fullNumber === senderAccountNumber,
      );

      if (currentAccount && currencyId) {
        const response = (await getSenderAccountBalance({
          id: currentAccount.id,
          currencyId: currencyId as string,
        })) as { data: { data: { edges: { node: { value: number } }[] } } };

        if (response?.data?.data) {
          setSenderBalance(response.data.data.edges[0]?.node?.value);
        }
      }
    },
    [senderAccountNumber, currencyId, senderAccountsList],
  );

  const getRecipientAccountBalanceAction = useCallback(
    async (number?: string) => {
      const currentAccount = recipientAccountsList.find(
        (item) =>
          item.fullNumber === number ||
          item.fullNumber === recipientAccountNumber,
      );

      if (currentAccount && currencyId) {
        const response = (await getRecipientAccountBalance({
          id: currentAccount.id,
          currencyId: currencyId as string,
        })) as { data: { data: { edges: { node: { value: number } }[] } } };

        if (response?.data?.data) {
          setRecipientBalance(response.data.data.edges[0]?.node?.value);
        }
      }
    },
    [recipientAccountNumber, currencyId, recipientAccountsList],
  );

  const setCollapseAllRowsAction = () => {
    dispatch(setCollapseAllRows(true));
  };

  const onClearSidepanel = () => {
    dispatch(clearInternalTransactionState());
    dispatch(clearState());
    dispatch(clearStatusesState());
    dispatch(clearSignaturesState());
  };

  const {
    feeValue,
    setFeeValue,
    checkStatus,
    submitDisabled,
    setSubmitDisabled,
    isLoading,
    isUninitialized,
    editIsLoading,
    updateIsError,
    create,
    createSuccess,
    createInProgress,
    getTransactionsList,
    isWarningLoading,
    isWarningFetching,
    checkInProgress,
    isLoadingAml,
    applyTransactionsWarningIsLoading,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    onApplyTransactionAction,
    onClearTimer,
    internalError,
    onSubmit,
    getInternalTransactionByIdAmlAction,
    getStatusesTransactionById,
    getTransaction,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    getInternalTransactionWarningByIdAction,
    isWarningByIdFetching,
    isWarningByIdLoading,
    isWarningInfoLoading,
    selectedWarningId,
    setPostingDateWithTime,
    setTransferDateWithTime,
    postingDateWithTime,
    transferDateWithTime,
  } = useRequests({
    transactionId,
    warnings,
    transaction,
    activePermissions,
    setCheckError,
    fields,
    setError,
    setErrorMessage,
    setStatusChangeError,
    getValues,
    errors,
    isDirty,
    panelIsOpen,
    setStatusCheckError,
    setWarningActionError,
  });

  const handleClose = useCallback(() => {
    setCheckError('');
    dispatch(clearInternalTransactionState());
    dispatch(clearState());
    dispatch(clearSignaturesState());
    setUploadedFilesError([]);

    onClearTimer(true);

    setTimeout(() => {
      if (backPath) {
        navigate(backPath);
      } else {
        navigate(`${ROUTES.INTERNAL_TRANSACTION}${search || ''}`);
      }

      setBackPath('');
    }, 100);
  }, [backPath, search]);

  const {
    imgLink,
    deleteTransactionError,
    errorMessageSignature,
    setErrorMessageSignature,
    signaturePopupVisible,
    setSignaturePopupVisible,
    statusChangeInProgress,
    statusPopupVisible,
    setStatusPopupVisible,
    rejectPopupVisible,
    setRejectPopupVisible,
    deleteTransactionInProgress,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    editCommentInProgress,
    editCommentIsSuccess,
    deleteCommentInProgress,
    commentInProgress,
    documentCategoryChangeInProgress,
    documentStatusChangeInProgress,
    documentIsLoading,
    documentIsFetching,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    documentsIsLoading,
    addDocumentInProgress,
    fileUploadIsSuccess,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    glTransactionIsLoading,
    glTransactionIsFetching,
    glIsLoading,
    fileUploadInProgress,
    transactionChatsIsLoading,
    chatCreateInProgress,
    isLoadingSignatures,
    createSignatureInProgress,
    signatureIsSuccess,
    sendCodeInProgress,
    validateCodeInProgress,
    changeStatusIsSuccess,
    changeStatusIsLoading,
    isLoadingLogs,
    onDeleteTransaction,
    getTransactionDocumentyIdAction,
    onGlTransactionClick,
    onUploadDocument,
    onCreateTransactionChat,
    createChatWithMessage,
    onSendCode,
    validateCodeAction,
    getLogsTransactionByIdAction,
    onClearRequestTimer,
    onChangeStatusPopup,
    onSignatureCancelClick,
    onChangeStatus: onNextStatus,
    getTransationDocumentsAction,
    getGeneralLedgerTransactionValuesAction,
    getTransactionsChatsAction,
    getSignaturesTransactionByIdAction,
    onReject,
    onDeleteDocument,
    onMoveDocument,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    onDeleteComment,
    onChangeCustomerDocumentsStatus,
    sendCodeRetry,
    sendCode,
    onClearRequestTimeout,
    getTransactionChangeLogsAction,
    changeLogsIsFetching,
    changeLogsIsLoading,
    documentsIsFetching,
    attachedDocumentsErrors,
    setAttachedDocumentsErrors,
    uploadedDocumentsErrors,
    setUploadedDocumentsErrors,
    getTransactionCommentsAction,
    commentsIsLoading,
    commentsIsFetching,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
  } = useBaseRequests({
    setError,
    transactionId,
    transaction,
    currentEmployee,
    sendMessage,
    setErrorMessage,
    createdTransactionId,
    statusUrl: StatusesUrl.INTERNAL,
    setStatusChangeError,
    onSubmit,
    getTransactionsList,
    getStatusesTransactionById,
    onClearTimer,
    getTransaction,
    reset,
    handleClose,
    onClear: onClearSidepanel,
    chatTitle: 'RFI: Internal transaction',
    takeCount: selectedCount,
    search,
    transactionListUrl: ROUTES.INTERNAL_TRANSACTION,
    errors,
    isDirty,
    documents,
    documentItem,
    setCollapseAllRows: setCollapseAllRowsAction,
    updateIsError,
    expandedGLTransaction,
    setUploadedFilesError,
    transactionUrl: 'internal',
  });

  const prevSignaturePopupVisible = usePrevious(signaturePopupVisible);

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    switch (name) {
      case 'recipientAccountNumber':
        onSearchRecipientAccounts(searchAutocomplete);
        break;
      case 'senderAccountNumber':
        onSearchSenderAccounts(searchAutocomplete);
        break;
      default:
        break;
    }
  };

  const sendCodeAction = handleSubmit(async (values) => {
    setErrorMessage('');

    let createResponse;

    if (!transactionId && !Object.keys(errors).length) {
      const body: {
        transferDate?: string;
        sum: number;
        currencyId: string;
        senderAccountNumber: string;
        recipientAccountNumber: string;
        documentNumber: string;
        info: string;
        depositDateOfDestinationAccount?: string;
      } = {
        sum: Number(values.sum),
        currencyId: values.currencyId,
        senderAccountNumber: values.senderAccountNumber,
        recipientAccountNumber: values.recipientAccountNumber,
        documentNumber: values.documentNumber,
        info: values.info,
      };

      if (values.transferDate) {
        const date = new Date(values.transferDate);

        if (transferDateWithTime) {
          body.transferDate = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);

          body.transferDate = date.toISOString();
        }
      }

      if (values?.depositDateOfDestinationAccount) {
        const date = new Date(values.depositDateOfDestinationAccount);

        if (postingDateWithTime) {
          body.depositDateOfDestinationAccount = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);

          body.depositDateOfDestinationAccount = date.toISOString();
        }
      }

      createResponse = (await create(body)) as {
        data?: { data: BaseTransaction };
        error: ResponseError;
      };
    }

    if (createResponse && createResponse.error) {
      responseErrorsResolver({
        errors: createResponse.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    } else if (createResponse && createResponse.data) {
      if (createResponse && createResponse.data) {
        setCreatedTransactionId(createResponse.data.data.id);
      }

      if (currentEmployee?.canSignTransactions) {
        const response = (await sendCode({ mfa: MfaType.TOTP })) as {
          data: { data: MfaToken };
        };

        if (response && response.data && response.data.data) {
          setSignaturePopupVisible(true);

          const expireTime = Math.round(
            new Date(response.data.data.expiresAt).getTime() - Date.now(),
          );

          sendCodeRetry(expireTime);
        }
      }
    }
  });

  useEffect(() => {
    if (senderAccountNumber && senderAccountsList && !transactionId) {
      const currentAccount = senderAccountsList.find(
        (item) => item.fullNumber === senderAccountNumber,
      );

      if (currentAccount) {
        getAllSenderAccountBalancesAction(currentAccount.id);
      }
    }
  }, [senderAccountNumber, senderAccountsList, transactionId]);

  useEffect(() => {
    if (recipientAccountNumber && recipientAccountsList && !transactionId) {
      const currentAccount = recipientAccountsList.find(
        (item) => item.fullNumber === recipientAccountNumber,
      );

      if (currentAccount) {
        getAllRecipientAccountBalancesAction(currentAccount.id);
      }
    }
  }, [recipientAccountNumber, recipientAccountsList, transactionId]);

  useEffect(() => {
    if (senderAccountNumber && currencyId && senderAccounts) {
      getSenderAccountBalanceAction(senderAccountNumber);
    }
  }, [senderAccountNumber, currencyId, senderAccounts]);

  useEffect(() => {
    if (recipientAccountNumber && currencyId && recipientAccounts) {
      getRecipientAccountBalanceAction(recipientAccountNumber);
    }
  }, [recipientAccountNumber, currencyId, recipientAccounts]);

  useEffect(() => {
    if (senderBalances && recipientBalances && currencies && baseCurrency) {
      const availableCurrenciesList: SelectItemProps[] = [];

      if (
        senderBalances?.find((item) => item.currencyId === baseCurrency.id) &&
        recipientBalances?.find((item) => item.currencyId === baseCurrency.id)
      ) {
        availableCurrenciesList.push({
          name: baseCurrency.name,
          value: baseCurrency.id,
          suffix: baseCurrency.description,
        });
      }

      currencies
        ?.filter((item) => item.value !== baseCurrency?.id)
        ?.forEach((item) => {
          if (
            senderBalances.find(
              (balance) => balance.currencyId === item.value,
            ) &&
            recipientBalances.find(
              (balance) => balance.currencyId === item.value,
            )
          ) {
            availableCurrenciesList.push(item);
          }
        });

      setAvailableCurrencies(availableCurrenciesList);
    }
  }, [senderBalances, recipientBalances, currencies, baseCurrency]);

  useEffect(() => {
    if (transaction?.senderAccount) {
      setSenderAccountsList([transaction?.senderAccount]);
      setSenderAutocompleteList([
        {
          name: transaction?.senderAccount.description,
          description: '',
          value: transaction?.senderAccount.fullNumber,
          suffix: `${transaction?.senderAccount.fullNumber} ${
            // eslint-disable-next-line no-nested-ternary
            overdraftEnabled
              ? transaction?.senderAccount?.overdraftEnabled
                ? '(Overdraft)'
                : ''
              : ''
          }`,
        },
      ]);
    }
  }, [transaction]);

  useEffect(() => {
    if (transaction?.recipientAccount) {
      setRecipientAccountsList([transaction?.recipientAccount]);
      setRecipientAutocompleteList([
        {
          name: transaction?.recipientAccount.description,
          description: '',
          value: transaction?.recipientAccount.fullNumber,
          suffix: `${transaction?.recipientAccount.fullNumber} ${
            // eslint-disable-next-line no-nested-ternary
            overdraftEnabled
              ? transaction?.recipientAccount?.overdraftEnabled
                ? '(Overdraft)'
                : ''
              : ''
          }`,
        },
      ]);
    }
  }, [transaction]);

  useEffect(() => {
    if (
      currencyId &&
      senderAccountNumber &&
      senderBalances &&
      senderBalances?.length &&
      !senderBalances?.find((item) => item.currencyId === currencyId)
    ) {
      setValue('currencyId', '');
    }
  }, [currencyId, senderBalances, senderAccountNumber]);

  useEffect(() => {
    if (transactionId) {
      dispatch(changePanelState({ state: true, transactionId }));
    } else {
      dispatch(changePanelState({ state: true }));
    }

    return () => {
      dispatch(clearInternalTransactionState());
      dispatch(clearState());
      dispatch(clearStatusesState());
      dispatch(clearSignaturesState());

      onClearTimer(true);
      onClearRequestTimeout();
    };
  }, [dispatch]);

  useEffect(() => {
    if (transferDate) {
      const formattedTransferDate = formatFormDate(transferDate);
      const formattedCurrentDate = formatFormDate(new Date().toISOString());

      if (transferDate && formattedTransferDate > formattedCurrentDate) {
        setError('transferDate', {
          type: 'custom',
          message: 'Creation Date cannot be later than current date',
        });
      } else {
        clearErrors('transferDate');
      }
    }
  }, [transferDate]);

  useEffect(() => {
    if (panelIsOpen && transactionId) {
      getTransaction();
    }
  }, [panelIsOpen, transactionId, activePermissions]);

  const onDuplicate = () => {
    onClearRequestTimer();
    onClearTimer(true);
    setCheckError('');
    dispatch(clearInternalTransactionState());
    dispatch(clearState());
    setUploadedFilesError([]);
    reset({});

    setTimeout(() => {
      navigate(`${ROUTES.INTERNAL_TRANSACTION}${search || ''}`);
    }, 100);

    setTimeout(() => {
      navigate(`${ROUTES.INTERNAL_TRANSACTION_CREATE}${search || ''}`, {
        state: {
          ...transaction,
          duplicate: true,
        },
      });
    }, 200);
  };

  useEffect(() => {
    if (!prevSignaturePopupVisible && errorMessageSignature) {
      setErrorMessageSignature('');
    }
  }, [prevSignaturePopupVisible, errorMessageSignature]);

  useEffect(() => {
    if (locationState && !locationState.duplicate) {
      setBackPath(locationState);
    }
  }, [locationState]);

  useEffect(() => {
    if (transaction) {
      setSubmitDisabled(false);

      getAllSenderAccountBalancesAction(
        transaction?.senderAccount?.id as string,
      );
      getAllRecipientAccountBalancesAction(
        transaction?.recipientAccount?.id as string,
      );

      reset({
        ...transaction,
        sum: transaction?.sum?.toString(),
      });

      if (transaction.sum) {
        setAmount(transaction.sum.toString());
      }

      if (transaction.feeAmount) {
        setFeeValue(transaction.feeAmount);
      }

      if (transaction?.senderAccountNumber && senderAccountsList.length) {
        getSenderAccountBalanceAction(transaction.senderAccountNumber);
      }

      if (transaction.recipientAccountNumber && recipientAccountsList.length) {
        getRecipientAccountBalanceAction(transaction.recipientAccountNumber);
      }
    }
  }, [transaction, senderAccountsList, recipientAccountsList]);

  useEffect(() => {
    if (
      changeStatusIsSuccess &&
      (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
        transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
        transaction?.baseellaStatus === BaseellaStatus.REJECTED)
    ) {
      setTimeout(() => {
        getTransactionsList(
          `?take=${selectedCount?.toString() || '25'}${search.replace(
            '?',
            '&',
          )}`,
        );
      }, 1500);
    }

    if (
      (signatureIsSuccess && !transactionId) ||
      (changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED))
    ) {
      dispatch(clearInternalTransactionState());
      dispatch(clearState());

      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});

      handleClose();
      setAttachedDocumentsErrors(undefined);
      setUploadedDocumentsErrors([]);

      if (
        changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED)
      ) {
        setTimeout(() => {
          getTransactionsList(
            `?take=${selectedCount?.toString() || '25'}${search.replace(
              '?',
              '&',
            )}`,
          );
        }, 1500);
      }
    }
  }, [transaction, signatureIsSuccess, transactionId]);

  useEffect(() => {
    if (signatureIsSuccess && transactionId) {
      onClearTimer(true);
      onClearRequestTimeout();
      setSignaturePopupVisible(false);

      setTimeout(() => {
        getTransactionsList(
          `?take=${selectedCount?.toString() || '25'}${search.replace(
            '?',
            '&',
          )}`,
        );
      }, 1500);

      getTransaction();
    }
  }, [signatureIsSuccess, transactionId]);

  useEffect(() => {
    if (
      currentEmployee &&
      !currentEmployee.canSignTransactions &&
      createSuccess
    ) {
      handleClose();
      setAttachedDocumentsErrors(undefined);
      setUploadedDocumentsErrors([]);
    }
  }, [currentEmployee, createSuccess]);

  useEffect(() => {
    if (transactionId && isDirty) {
      onClearTimer();
    }
  }, [isDirty, transactionId]);

  useEffect(() => {
    if (!prevSignaturePopupVisible && errorMessageSignature) {
      setErrorMessageSignature('');
    }
  }, [prevSignaturePopupVisible, errorMessageSignature]);

  useEffect(() => {
    if (locationState && locationState.duplicate && !fields.currencyId) {
      if (!fields.recipientAccountNumber) {
        getRecipientAccountBalanceAction(locationState.recipientAccountNumber);

        setRecipientAccountsList([locationState?.recipientAccount]);
        setRecipientAutocompleteList([
          {
            name: locationState?.recipientAccount.description,
            description: '',
            value: locationState?.recipientAccount.fullNumber,
            suffix: `${locationState?.recipientAccount.fullNumber} ${
              // eslint-disable-next-line no-nested-ternary
              overdraftEnabled
                ? locationState?.recipientAccount?.overdraftEnabled
                  ? '(Overdraft)'
                  : ''
                : ''
            }`,
          },
        ]);
      }

      if (!fields.senderAccountNumber) {
        getSenderAccountBalanceAction(locationState.senderAccountNumber);

        setSenderAccountsList([locationState?.senderAccount]);
        setSenderAutocompleteList([
          {
            name: locationState?.senderAccount.description,
            description: '',
            value: locationState?.senderAccount.fullNumber,
            suffix: `${locationState?.senderAccount.fullNumber} ${
              // eslint-disable-next-line no-nested-ternary
              overdraftEnabled
                ? locationState?.senderAccount?.overdraftEnabled
                  ? '(Overdraft)'
                  : ''
                : ''
            }`,
          },
        ]);
      }

      reset({
        recipientAccountNumber: locationState.recipientAccountNumber,
        senderAccountNumber: locationState.senderAccountNumber,
        sum: locationState?.sum?.toString(),
        currencyId: locationState.currencyId,
        documentNumber: locationState.documentNumber,
        info: locationState.info,
      });

      setAmount(locationState?.sum?.toString());
    }
  }, [locationState]);

  let feeText;

  if (feeValue === undefined) {
    feeText = 'Fee will be calculated after filling in the required fields';
  } else if (fields.currencyId) {
    feeText = `Fee: ${formatBalance(feeValue)} ${
      currencies?.find((item) => item.value === fields.currencyId)?.name
    }`;
  }

  const isLoadingSidepanel = useMemo(() => {
    let res = true;

    let isLoadingSidepanelVariables =
      !isLoading && !isUninitialized && !isWarningLoading;

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INCOMING_EDIT_ACTION,
      )
    ) {
      isLoadingSidepanelVariables =
        isLoadingSidepanelVariables &&
        !isLoadingStatuses &&
        !isUninitializedStatuses;
    }

    if (transactionId) {
      if (isLoadingSidepanelVariables) {
        res = false;
      }
    } else {
      res = false;
    }

    return res;
  }, [
    transactionId,
    isLoading,
    isLoadingStatuses,
    isUninitialized,
    isUninitializedStatuses,
    activePermissions,
    isWarningLoading,
  ]);

  const onSendCodeAction = !transactionId ? sendCodeAction : onSendCode;

  return (
    <InternalSidepanel
      enableEdit
      isLoading={
        isLoadingSidepanel ||
        isWarningLoading ||
        (!warnings && isWarningFetching) ||
        Boolean(!transaction && transactionId) ||
        isWarningInfoLoading ||
        Boolean(!warningsInfo && transactionId)
      }
      sidepanelExpandedKey="internal-transaction"
      internalError={internalError}
      panelIsOpen={panelIsOpen}
      handleClose={() => {
        handleClose();
        setAttachedDocumentsErrors(undefined);
        setUploadedDocumentsErrors([]);
      }}
      title={
        transactionId ? 'Internal Transaction' : 'New Internal Transaction'
      }
      handleSubmit={onSubmit}
      control={control as Control<any>}
      statusesInfo={transactionId ? statuses : undefined}
      onNextStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_INTERNAL_EDIT_ACTION,
        )
          ? (status: string) => onNextStatus(status)
          : undefined
      }
      transactionId={transactionId}
      onChangeStatusPopup={onChangeStatusPopup}
      setStatusPopupVisible={setStatusPopupVisible}
      statusPopupVisible={statusPopupVisible}
      isCreateForm={!transactionId}
      isDisabledSaveButton={
        !isDirty ||
        editIsLoading ||
        createInProgress ||
        submitDisabled ||
        sendCodeInProgress ||
        checkInProgress ||
        checkStatus === CheckTransactionStatus.RED ||
        applyTransactionsWarningIsLoading
      }
      createAllowed={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INTERNAL_CREATE_ACTION,
      )}
      isEditable={
        transactionId
          ? activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_INTERNAL_EDIT_ACTION,
            )
          : activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_INTERNAL_CREATE_ACTION,
            )
      }
      transactionInfo={transaction}
      amlInfo={aml}
      currencies={availableCurrencies}
      errors={errors && Object.keys(errors).length ? errors : undefined}
      recipientAccounts={recipientAccounts}
      senderAccounts={senderAccounts}
      onSearchAutocomplete={onSearchAutocomplete}
      recipientAccountsIsLoading={recipientAccountsIsLoading}
      senderAccountsIsLoading={senderAccountsIsLoading}
      baseellaStatus={transaction?.baseellaStatus}
      logs={statusLogs}
      feeText={feeText}
      warnings={warnings}
      employee={employee}
      onApplyTransactionAction={onApplyTransactionAction}
      changeLogs={transactionChangeLogs}
      senderAccountBalance={senderBalance}
      recipientAccountBalance={recipientBalance}
      currencyId={currencyId}
      senderBalanceIsLoading={
        senderAccountBalanceIsLoading || senderAccountBalanceIsFetching
      }
      recipientBalanceIsLoading={
        recipientAccountBalanceIsLoading || recipientAccountBalanceIsFetching
      }
      updateSenderBalance={getSenderAccountBalanceAction}
      updateRecipientBalance={getRecipientAccountBalanceAction}
      isDirty={isDirty}
      checkInProgress={checkInProgress}
      signaturePopupVisible={signaturePopupVisible}
      sendCode={onSendCodeAction}
      validateCode={validateCodeAction}
      validateCodeInProgress={
        validateCodeInProgress || createSignatureInProgress
      }
      signatures={signatures}
      errorSidepanel={errorMessage}
      rejectPopupVisible={rejectPopupVisible}
      setRejectPopupVisible={setRejectPopupVisible}
      onReject={onReject}
      rejectInProgress={
        changeStatusIsLoading || applyTransactionsWarningIsLoading
      }
      updateStatusIsLoading={
        statusChangeInProgress || applyTransactionsWarningIsLoading
      }
      isLoadingStatuses={isFetchingStatuses}
      errorMessageSignature={errorMessageSignature}
      transactionsTypes={transactionsTypes}
      checkError={
        statusCheckError ||
        (checkStatus === CheckTransactionStatus.RED
          ? 'Transaction could not be completed'
          : undefined)
      }
      transactionCheckError={checkError}
      onSignatureCancelClick={onSignatureCancelClick}
      activePermission={activePermissions}
      onChangeStatusPopupLoading={editIsLoading || changeStatusIsLoading}
      amount={amount}
      setAmount={setAmount}
      createdByOrganizationId={transaction?.createdByOrganizationId}
      latestQueuedStatus={transaction?.latestQueuedStatuses?.baseellaStatus}
      statusChangeError={statusChangeError}
      applyTransactionsWarningIsLoading={applyTransactionsWarningIsLoading}
      chats={chats}
      onCreateTransactionChat={onCreateTransactionChat}
      chatCreateInProgress={chatCreateInProgress}
      currentEmployee={currentEmployee}
      createChatWithMessage={createChatWithMessage}
      documents={documents}
      document={documentItem}
      documentFileIsLoading={downloadInProgress || downloadIsFetching}
      downloadInProgress={downloadInProgress || downloadIsFetching}
      imgLink={imgLink}
      onDelete={onDeleteDocument}
      deleteInProgress={deleteDocumentInProgress}
      deleteIsSuccess={deleteDocumentIsSuccess}
      onMove={onMoveDocument}
      moveInProgress={documentCategoryChangeInProgress}
      onDownload={onDownloadDocument}
      sendComment={onSendDocumentComment}
      editComment={onEditComment}
      editCommentInProgress={editCommentInProgress}
      editCommentIsSuccess={editCommentIsSuccess}
      deleteComment={onDeleteComment}
      deleteCommentInProgress={deleteCommentInProgress}
      commentInProgress={commentInProgress}
      changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
      uploadDocumentErrors={uploadedFilesError}
      uploadDocumentInProgress={fileUploadInProgress || addDocumentInProgress}
      uploadDocumentIsSuccess={fileUploadIsSuccess}
      onUploadDocument={onUploadDocument}
      onDownloadSuccess={downloadFileIsSuccess}
      getTransactionDocument={getTransactionDocumentyIdAction}
      documentIsLoading={documentIsLoading || documentIsFetching}
      glTransactionsIsLoading={glIsLoading}
      glTransactionIsLoading={glTransactionIsLoading || glTransactionIsFetching}
      glTransactions={glTransactions}
      onGlTransactionClick={onGlTransactionClick}
      glTransaction={expandedGLTransaction}
      clearGlTransactionState={() =>
        dispatch(clearExpandedGeneralLedgerTransactionState())
      }
      deleteIsVisible={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_DOCUMENTS_DELETE_ACTION,
      )}
      expandedGLTransactionCreditBalance={expandedGLTransactionCreditBalance}
      expandedGLTransactionDebitBalance={expandedGLTransactionDebitBalance}
      expendedDebitBalanceIsLoading={
        debitBalanceIsLoading || debitBalanceIsFetching
      }
      expendedCreditBalanceIsLoading={
        creditBalanceIsLoading || creditBalanceIsFetching
      }
      deleteTransactionError={deleteTransactionError}
      onDeleteTransactions={onDeleteTransaction}
      deleteTransactionInProgress={deleteTransactionInProgress}
      updateIsLoading={editIsLoading}
      createIsLoading={createInProgress}
      canSignTransaction={currentEmployee?.canSignTransactions}
      deleteTransactionDisabled={
        !activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DELETE_ACTION,
        )
      }
      sendCodeInProgress={sendCodeInProgress}
      senderBalances={senderBalances}
      recipientBalances={recipientBalances}
      currenciesList={currenciesList}
      onDuplicate={onDuplicate}
      duplicate={locationState?.duplicate}
      getTransactionDocuments={getTransationDocumentsAction}
      documentsIsLoading={documentsIsLoading}
      getGeneralLedgerTransactionValues={
        getGeneralLedgerTransactionValuesAction
      }
      getTransactionsChats={getTransactionsChatsAction}
      transactionChatsIsLoading={transactionChatsIsLoading}
      getSignaturesTransactionById={getSignaturesTransactionByIdAction}
      isLoadingSignatures={isLoadingSignatures}
      getInternalTransactionByIdAmlAction={getInternalTransactionByIdAmlAction}
      isLoadingAml={isLoadingAml}
      getLogsTransactionById={getLogsTransactionByIdAction}
      isLoadingLogs={isLoadingLogs}
      overdraftEnabled={overdraftEnabled}
      recipientAccountsWithInfo={recipientAccountsList}
      senderAccountsWithInfo={senderAccountsList}
      fields={fields}
      isWarning={transaction && !isWarningLoading && Boolean(warnings?.length)}
      sendMessage={sendMessage}
      recipientCompanyOfficials={recipientCompanyOfficials}
      senderCompanyOfficials={senderCompanyOfficials}
      documentStatusChangeInProgress={documentStatusChangeInProgress}
      allCurrenciesList={currencies}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      changeLogsIsLoading={changeLogsIsLoading}
      users={users}
      transactionChangeLogsPagination={transactionChangeLogsPagination}
      changeLogsIsFetching={changeLogsIsFetching}
      onResetTransactionWarningAction={onResetTransactionWarningAction}
      resetTransactionsWarningIsLoading={resetTransactionsWarningIsLoading}
      resetTransactionsWarningIsSuccess={resetTransactionsWarningIsSuccess}
      documentsPagination={documentsPagination}
      documentsIsFetching={documentsIsFetching}
      transferDate={transferDate}
      attachedDocumentsErrors={attachedDocumentsErrors}
      uploadedDocumentsErrors={uploadedDocumentsErrors}
      transactionComments={transactionComments}
      transactionCommentsPagination={transactionCommentsPagination}
      getTransactionCommentsAction={getTransactionCommentsAction}
      commentsIsLoading={commentsIsLoading}
      commentsIsFetching={commentsIsFetching}
      onAddCommentToTransaction={onAddCommentToTransaction}
      addCommentInProgress={addCommentInProgress}
      addCommentIsSuccess={addCommentIsSuccess}
      onEditCommentInTransaction={onEditCommentInTransaction}
      editTransactionCommentInProgress={editTransactionCommentInProgress}
      editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
      onDeleteCommentInTransaction={onDeleteCommentInTransaction}
      deleteCommentInTransactionInProgress={
        deleteCommentInTransactionInProgress
      }
      deleteCommentInTranscationIsSuccess={deleteCommentInTranscationIsSuccess}
      getInternalTransactionWarningById={
        getInternalTransactionWarningByIdAction
      }
      isWarningByIdFetching={isWarningByIdFetching}
      isWarningByIdLoading={isWarningByIdLoading}
      warningsInfo={warningsInfo}
      selectedWarningId={selectedWarningId}
      warningActionError={warningActionError}
      clearErrors={clearErrors}
      deleteDisabled={ffSettings?.ffDisableTxGlRemoval}
      withTime={{
        postingDateWithTime,
        transferDateWithTime,
      }}
      setWithTime={{
        setPostingDateWithTime,
        setTransferDateWithTime,
      }}
    />
  );
};

export default InternalTransaction;
